import { FC, ReactNode } from 'react'

import { clsx } from 'clsx'

import { Button } from '../Button/index.js'
import { SvgIcon } from '../Icon/SvgIcon.js'
import { Modal } from '../Modal/index.js'
import { Text } from '../Text/index.js'

type ConfirmableProps = {
  proceed: (value: boolean) => void
  show: boolean
}

export type CreateConfirmationOptions = {
  title: string
  description?: string | ReactNode
  proceedLabel?: string
  cancelLabel?: string
  danger?: boolean
  actions?: (confirm: () => void, dismiss: () => void) => ReactNode
}

export type ConfirmDialogProps = ConfirmableProps & CreateConfirmationOptions

/**
 *
 */
export const ConfirmDialog: FC<ConfirmDialogProps> = props => {
  const {
    show,
    proceed,
    title,
    description,
    proceedLabel,
    cancelLabel,
    danger = false,
    actions,
  } = props

  const dismiss = () => proceed(false)
  const confirm = () => proceed(true)

  return (
    <Modal open={show} onClose={dismiss} showCloseBtn={false}>
      <Modal.Content>
        <div className="sm:flex sm:items-start">
          <div
            className={clsx(
              'mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full sm:mx-0 sm:h-10 sm:w-10',
              !danger && 'bg-surface-subdued',
              danger && 'bg-surface-negative',
            )}
          >
            <SvgIcon
              size="lg"
              className={clsx(
                !danger && 'text-content-subdued',
                danger && 'text-content-on-negative',
              )}
              name="status-warning"
            />
          </div>
          <div className="mt-3 text-center sm:text-start sm:mt-0 sm:ms-4 min-w-0 break-words flex flex-col gap-3 pt-0 sm:pt-1">
            <Text variant="headingXs" as="h3">
              {title}
            </Text>
            {typeof description === 'string' ? (
              <Text variant="bodyLg">{description}</Text>
            ) : (
              description
            )}
          </div>
        </div>
      </Modal.Content>
      <Modal.Actions>
        {actions ? (
          actions(confirm, dismiss)
        ) : (
          <>
            <Button
              onClick={confirm}
              variant="primary"
              size="lg"
              destructive={danger}
              className="w-full sm:w-auto"
            >
              {proceedLabel || 'Confirm'}
            </Button>
            <Button
              onClick={dismiss}
              variant="secondaryNeutral"
              size="lg"
              className="w-full sm:w-auto"
            >
              {cancelLabel || 'Cancel'}
            </Button>
          </>
        )}
      </Modal.Actions>
    </Modal>
  )
}
